import { useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, generatePath, useSearchParams } from 'react-router-dom';

import { useConnections } from '@/entities/connection';
import { useDatasets, useLatestScores } from '@/features/catalog-browser';
import { scoreBand } from '@/features/scoring';
import { SCREEN_PATHS } from '@/shared/routes';

import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SegmentedFilter } from '@/shared/ui/SegmentedFilter';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { UnavailableState } from '@/shared/ui/States';

import {
  LINEAGE_LENSES,
  useImpact,
  useLineageNode,
  useNodeByDataset,
  useProvenance,
  useReviewEdge,
  useReviewQueue,
} from '../api/lineage';
import type { AffectedNode, AsOf, Edge, LineageLens, Node } from '../api/lineage';
import { completenessOf, layOut, systemsIn } from '../model/graph';
import { DatasetBar } from './DatasetBar';
import { DqImpactCard } from './DqImpactCard';
import { LineageGraph } from './LineageGraph';
import type { NodeHealth } from './LineageGraph';
import { LineageLegend } from './LineageLegend';
import { LineageFocusPanel } from './LineageFocusPanel';
import { LineageSourcePicker, LineageTablePicker } from './LineagePicker';
import { LineageSteps } from './LineageSteps';
import type { LineageStep } from './LineageSteps';
import { NativeLineagePanel } from './NativeLineagePanel';
import { asOfFromInput, confidencePercent } from '../model/presentation';

/** Whether a failed read was the server saying "no such thing" rather than failing to answer. */
function isNotFound(error: Error): boolean {
  return 'status' in error && error.status === 404;
}

/* Lineage: what breaks if this changes, what produced it, and as of when. */
export function LineageScreen(): ReactElement {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const datasetIdFromUrl = searchParams.get('datasetId');
  const nodeIdFromUrl = searchParams.get('nodeId');
  /* The chosen source, for the second step. A URL parameter for the same reason `asOf` and `lens`
     are: a link to "the tables of core-banking-pg" is shareable, and Back walks the steps. */
  const sourceFromUrl = searchParams.get('source');

  const [selectedDatasetId, setSelectedDatasetId] = useState<string>(
    datasetIdFromUrl ?? '',
  );
  const [customNodeId, setCustomNodeId] = useState<string>(nodeIdFromUrl ?? '');
  const [activeNodeId, setActiveNodeId] = useState<string | null>(nodeIdFromUrl);

  /* **The instant the walk answers for, read from `?asOf=` rather than typed into a card.**
   *
   * `as_of` is the reason `ImpactAnswer` carries it: "the graph as it was when that score was
   * computed" is a real question, and it is emphatically not the question anybody opens this screen
   * with. It had a form below the canvas, which the reference does not have; the URL keeps the
   * capability reachable and shareable — `?datasetId=…&asOf=2026-06-01T09:00` is a link to a
   * historical graph — without a control on a screen whose subject is the picture.
   *
   * Parsed through `asOfFromInput` so a malformed instant is `null` (the graph as it stands) rather
   * than a string the server has to refuse. */
  const asOf: AsOf = asOfFromInput(searchParams.get('asOf') ?? '');

  /* **Which channel the graph is drawn through, read from `?lens=` for the same reason `asOf` is.**
   * `?datasetId=…&lens=native` is a link to "what Snowflake itself reports for this table" that a
   * steward can share, and the toggle below is what writes it. An unrecognised value (a stale link,
   * a typo) falls back to `combined` rather than being sent to the server — the same posture
   * `asOfFromInput` takes for a malformed instant. */
  const lensFromUrl = searchParams.get('lens');
  const lens: LineageLens = (LINEAGE_LENSES as readonly string[]).includes(
    lensFromUrl ?? '',
  )
    ? (lensFromUrl as LineageLens)
    : 'combined';

  const handleLensChange = (nextLens: LineageLens): void => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      /* `combined` is the server default, so an explicit `combined` and an absent parameter mean
         the same thing — dropped rather than written, for the shorter, more shareable URL. */
      if (nextLens === 'combined') {
        next.delete('lens');
      } else {
        next.set('lens', nextLens);
      }
      return next;
    });
  };

  const nodeByDataset = useNodeByDataset(selectedDatasetId || null);
  /* The same cached catalogue read the picker makes, for the rail's labels: which source a chosen
     dataset belongs to, and its name. No extra request once the picker has loaded. */
  const datasets = useDatasets({ search: '', connectionId: null });
  const connections = useConnections();
  const selectedRow = datasets.data?.items.find((row) => row.id === selectedDatasetId);
  const sourceId = sourceFromUrl ?? selectedRow?.connection_id ?? null;
  const sourceName =
    sourceId === null
      ? null
      : (connections.data?.items.find((each) => each.id === sourceId)?.name ?? null);

  useEffect(() => {
    /* `selectedDatasetId` is declared even though the effect only reacts to the URL: the body reads
     * it, and an undeclared read is how an effect comes to act on a stale value. Safe against a loop
     * because the guard is an inequality — a re-run with them equal does nothing. */
    if (datasetIdFromUrl && datasetIdFromUrl !== selectedDatasetId) {
      setSelectedDatasetId(datasetIdFromUrl);
    }
  }, [datasetIdFromUrl, selectedDatasetId]);

  useEffect(() => {
    if (selectedDatasetId) {
      if (nodeByDataset.data) {
        setActiveNodeId(nodeByDataset.data.id);
      }
    } else if (customNodeId.trim()) {
      setActiveNodeId(customNodeId.trim());
    } else {
      setActiveNodeId(null);
    }
  }, [selectedDatasetId, nodeByDataset.data, customNodeId]);

  /* The step is derived from what has been chosen, never stored: a dataset (or a `?nodeId=`) means
     the graph, a source alone means its tables, nothing means the sources. So a shared link lands on
     the right step, and Back in the browser walks the steps because each one is a URL. */
  const step: LineageStep =
    selectedDatasetId !== '' || activeNodeId !== null ? 3 : sourceId !== null ? 2 : 1;

  const pickSource = (connectionId: string): void => {
    setSearchParams({ source: connectionId });
  };

  const handleDatasetChange = (newDatasetId: string, source?: string): void => {
    setSelectedDatasetId(newDatasetId);
    setCustomNodeId('');
    if (newDatasetId) {
      setSearchParams(
        source === undefined
          ? { datasetId: newDatasetId }
          : { source, datasetId: newDatasetId },
      );
    } else {
      setSearchParams(source === undefined ? {} : { source });
      setActiveNodeId(null);
    }
  };

  const goToStep = (target: LineageStep): void => {
    if (target === 1) handleDatasetChange('');
    if (target === 2) handleDatasetChange('', sourceId ?? undefined);
  };

  return (
    <>
      <PageHeader
        title={t('lineage.title')}
        help={t('help.feature.lineage')}
        lede={t('lineage.description')}
      />

      <div className="lq-ln">
        <LineageSteps
          step={step}
          connectionName={sourceName}
          datasetName={
            selectedRow === undefined
              ? null
              : `${selectedRow.schema_name}.${selectedRow.object_name}`
          }
          onStep={goToStep}
        />

        {step === 1 ? (
          <>
            {/* **The search across every source, kept on the first step.** The design's steps
                narrow source by source; this answers "I know the table, not where it lives" in one
                interaction, searching name and connection together. It is the only search on this
                step, and the next step's filter only narrows within one source. */}
            <DatasetBar selectedDatasetId={null} onPick={handleDatasetChange} />
            <LineageSourcePicker onPick={pickSource} />
          </>
        ) : null}

        {step === 2 && sourceId !== null ? (
          <LineageTablePicker
            connectionId={sourceId}
            onPick={(datasetId) => {
              handleDatasetChange(datasetId, sourceId);
            }}
          />
        ) : null}

        {step === 3 ? (
          selectedDatasetId && nodeByDataset.isPending ? (
            <SkeletonRows />
          ) : selectedDatasetId &&
            nodeByDataset.isError &&
            !isNotFound(nodeByDataset.error) ? (
            /* Only a 404 means the graph has never heard of this table. Any other failure is a
               failure of the request, and saying "no lineage recorded" for it would be a claim
               about the data from a read that did not happen. */
            <UnavailableState
              title={t('lineage.node.errorTitle')}
              guidance={nodeByDataset.error.message}
            />
          ) : selectedDatasetId && nodeByDataset.isError ? (
            /* The graph has never heard of this table. Said as the design's card with the way to
               the dataset itself, because that is where its schema and profile still are. */
            <div className="lq-ln__noNode">
              <h2 className="lq-ln__noNodeTitle">
                {t('lineage.pending.title', {
                  name:
                    selectedRow === undefined
                      ? t('lineage.pending.thisTable')
                      : `${selectedRow.schema_name}.${selectedRow.object_name}`,
                })}
              </h2>
              <p className="lq-ln__noNodeBody">{t('lineage.pending.body')}</p>
              <Link
                className="lq-btn lq-btn--secondary lq-btn--compact"
                to={generatePath(SCREEN_PATHS.dataset, { datasetId: selectedDatasetId })}
              >
                {t('lineage.focus.openDataset')}
              </Link>
            </div>
          ) : activeNodeId === null ? (
            <EmptyState
              title={t('lineage.empty.title')}
              guidance={t('lineage.empty.guidance')}
            />
          ) : (
            <NodeLineage
              nodeId={activeNodeId}
              asOf={asOf}
              lens={lens}
              onLensChange={handleLensChange}
            />
          )
        ) : null}

        <ReviewQueue />
      </div>
    </>
  );
}

function NodeLineage({
  nodeId,
  asOf,
  lens,
  onLensChange,
}: {
  readonly nodeId: string;
  readonly asOf: AsOf;
  readonly lens: LineageLens;
  readonly onLensChange: (lens: LineageLens) => void;
}): ReactElement {
  const { t } = useTranslation();
  const node = useLineageNode(nodeId);
  const impact = useImpact(nodeId, asOf, lens);
  const provenance = useProvenance(nodeId, asOf, lens);
  /* Which node the reader picked in the graph. `null` means no selection, which is a third state
     rather than "the subject": with nothing selected the whole graph is at full strength, and
     selecting the subject dims neither side. */
  const [picked, setPicked] = useState<string | null>(nodeId);
  const reviewQueue = useReviewQueue();

  const affectedUp = useMemo(() => provenance.data?.affected ?? [], [provenance.data]);
  const affectedDown = useMemo(() => impact.data?.affected ?? [], [impact.data]);
  const layout = useMemo(
    () => (node.data === undefined ? null : layOut(node.data, affectedUp, affectedDown)),
    [node.data, affectedUp, affectedDown],
  );
  /* Every dataset the picture drew, scored in one batch read for the health dots. */
  const datasetIds = useMemo(
    () =>
      (layout?.nodes ?? []).flatMap((each) =>
        each.datasetId === null ? [] : [each.datasetId],
      ),
    [layout],
  );
  const scores = useLatestScores(datasetIds);
  const health = useMemo(() => {
    const bands = new Map<string, NodeHealth>();
    if (scores.data === undefined) return bands;
    for (const [id, summary] of scores.data) {
      if (summary.defensible && summary.value !== null) {
        const band = scoreBand(summary.value);
        bands.set(id, band === 'danger' ? 'error' : band);
      }
    }
    return bands;
  }, [scores.data]);
  /* The review queue's inferred edges that touch this picture: excluded from every walk until a
     person accepts them, so the legend is the only place the picture can say they exist. */
  const inferred = useMemo(() => {
    if (!reviewQueue.isSuccess || layout === null) return null;
    const ids = new Set(layout.nodes.map((each) => each.id));
    return reviewQueue.data.edges.filter(
      (edge) => ids.has(edge.upstream_id) || ids.has(edge.downstream_id),
    ).length;
  }, [reviewQueue.isSuccess, reviewQueue.data, layout]);

  if (node.isPending) return <SkeletonRows />;
  if (node.isError) {
    return (
      <Alert tone="error" title={t('lineage.node.errorTitle')}>
        {t('lineage.node.errorBody')}
      </Alert>
    );
  }

  const subject = node.data;
  const truncation = completenessOf(provenance.data, impact.data);

  return (
    <>
      {/* **The graph, and nothing else.** This screen was the graph plus five more panels — a
          friendly overview, the node's own facts, an impact ring list, a provenance ring list, and
          the edges table — and the reference is one canvas. The extra panels each restated in words
          what the picture shows, and stacked they pushed the picture into being the top third of a
          page a reader had to scroll.

          What went, so it is a decision rather than an omission: `LineageOverview` (upstream and
          downstream at depth 1, which the graph draws), the node facts card (kind, external id,
          catalogued state), both `Answer` cards (the rings, which are `depth` — the graph's own
          columns), `NodeEdges` (one node's exact neighbours, the precise answer the layout's
          adjacent-depth approximation is not), and the node list under the canvas. The components
          are gone from this screen and their reads are gone with them: three fewer requests per
          view.

          **Two things deliberately survive, and both only appear when they have something to say.**
          The incompleteness alerts, because `ImpactAnswer.complete` exists so a reader is never
          shown a partial graph that looks whole — rendering a truncated graph silently is the one
          removal that would make this screen state something false. And the as-of control below,
          because temporal lineage is the reason `as_of` is on the wire at all. */}
      {/* The design's arrangement: the canvas in a card with its legend beneath it, and the
          selected node's panel beside it. The panel wraps under the canvas on a narrow screen.

          **Native | Platform | Combined sits on the canvas it changes**, in its top-left corner the
          way the zoom controls sit in the other: it narrows which edges the same picture draws and
          owns no panel of its own. `lens` is server-side, so switching never blends a native answer
          with a platform one client-side. */}
      <div className="lq-ln__view">
        <article className="lq-ln__canvas">
          <LineageGraph
            subject={subject}
            provenance={affectedUp}
            impact={affectedDown}
            selectedId={picked}
            onSelect={setPicked}
            health={health}
          />
          <div className="lq-ln__canvasFoot">
            <LineageLegend inferred={inferred} />
            <div className="lq-ln__lens">
              <SegmentedFilter
                variant="track"
                label={t('lineage.lens.label')}
                options={[
                  { id: 'combined' as const, label: t('lineage.lens.combined') },
                  { id: 'native' as const, label: t('lineage.lens.native') },
                  { id: 'platform' as const, label: t('lineage.lens.platform') },
                ]}
                value={lens}
                onChange={onLensChange}
              />
            </div>
          </div>
        </article>
        {layout === null ? null : (
          <LineageFocusPanel
            layout={layout}
            picked={picked}
            health={health}
            onClear={() => {
              setPicked(null);
            }}
          />
        )}
      </div>

      {/* **A lens with nothing to show is a fact about the channel, not an empty table.** Distinct
          from the truncation/restriction alerts below: this graph can be `complete` — nothing was
          cut off — and still empty under `platform` because every recorded flow here happens to be
          a native harvest. Collapsing the two would let "Lumiq has recorded nothing of its own yet"
          read as "the graph is broken", which is the absence-is-not-zero failure this platform
          holds apart everywhere else. Silent for `combined`, where an empty graph already means
          exactly what it says. */}
      {lens !== 'combined' && affectedUp.length === 0 && affectedDown.length === 0 ? (
        <Alert tone="notice" title={t('lineage.lens.emptyTitle')}>
          {t(
            lens === 'native' ? 'lineage.lens.emptyNative' : 'lineage.lens.emptyPlatform',
          )}
        </Alert>
      ) : null}

      {/* **Which systems the picture spans, said rather than filtered.** §21 of the brief asks that
          a connection filter never hide a cross-connection dependency; the strongest way to hold
          that is to offer no hiding at all, because a Databricks table feeding a Snowflake mart is
          the answer a unified graph exists to give. What a reader does need is to be told the
          picture crosses systems, or that it does not — two boxes from two connections otherwise
          read as one estate.

          Derived from the same `layOut` the canvas drew, so the sentence can never describe a
          different set of nodes than the picture. */}
      <SystemsNote subject={subject} provenance={affectedUp} impact={affectedDown} />

      {truncation.upstreamReason !== null ? (
        <Alert tone="warning" title={t('lineage.graph.incomplete')}>
          {t('lineage.graph.truncatedUp', { reason: truncation.upstreamReason })}
        </Alert>
      ) : null}
      {truncation.downstreamReason !== null ? (
        <Alert tone="warning" title={t('lineage.graph.incomplete')}>
          {t('lineage.graph.truncatedDown', { reason: truncation.downstreamReason })}
        </Alert>
      ) : null}

      {/* **Where the graph's edges came from, under the graph they came from.** The canvas draws an
          adjacent-depth approximation — `layOut` says so in its own words — and this is the exact
          edge list with the provider, confidence and window of each assertion. A reader who wants to
          know *why* the platform thinks two tables are connected has had nowhere to look until now.

          Ordered so the source-harvested assertions come first, because those are the ones a reader
          opening this section is checking. */}
      <NativeLineagePanel nodeId={nodeId} asOf={asOf} lens={lens} />

      {/* Below the graph and its own completeness warnings, never above: whether the *picture* is
          trustworthy is the more urgent question, and this card answers a second one — what would
          notice if the subject broke — using an answer (`impact`) the graph has already fetched. */}
      <DqImpactCard
        subject={subject}
        impact={impact.data}
        upstreamCount={provenance.data?.affected.length}
      />
    </>
  );
}

/** Which connections this graph spans, and how many nodes belong to none.
 *
 * A paragraph rather than a card, and it renders on every graph rather than only a crossing one:
 * "every node here is in `databricks · bricks`" is as load-bearing as the crossing case, because a
 * reader who is *not* told cannot tell a single-system graph from one they have misread.
 */
function SystemsNote({
  subject,
  provenance,
  impact,
}: {
  readonly subject: Node;
  readonly provenance: readonly AffectedNode[];
  readonly impact: readonly AffectedNode[];
}): ReactElement | null {
  const { t } = useTranslation();
  const systems = systemsIn(layOut(subject, provenance, impact));

  if (systems.names.length === 0 && systems.unnamed === 0) return null;

  return (
    <p className="lq-graph__hint">
      {systems.names.length > 1
        ? t('lineage.graph.crossConnection', { count: systems.names.length })
        : systems.names.length === 1
          ? t('lineage.graph.oneConnection', { name: systems.names[0] })
          : null}
      {systems.unnamed > 0 ? (
        <> {t('lineage.graph.unnamedSystems', { count: systems.unnamed })}</>
      ) : null}
    </p>
  );
}

function Endpoints({ edge }: { readonly edge: Edge }): ReactElement {
  const { t } = useTranslation();
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        flexWrap: 'wrap',
      }}
    >
      <StatusBadge tone="neutral" label={t(`lineage.kind.${edge.upstream_kind}`)} />
      <span title={edge.upstream_id}>{edge.upstream_name}</span>
      <span aria-hidden="true" style={{ color: 'var(--text-muted)' }}>
        →
      </span>
      <StatusBadge tone="neutral" label={t(`lineage.kind.${edge.downstream_kind}`)} />
      <span title={edge.downstream_id}>{edge.downstream_name}</span>
    </span>
  );
}

/** A confidence, or the reason there isn't one. Shared so the two tables cannot drift. */
function ConfidenceCell({ edge }: { readonly edge: Edge }): ReactElement {
  const { t } = useTranslation();
  const percent = confidencePercent(edge);

  /* Blank rather than 0% or 100% where the concept does not apply. A number on a producer's plain
     statement invites a reader to treat it as a probability. */
  if (percent === null) {
    return (
      <span style={{ color: 'var(--lumiq-text-muted)' }}>
        {t('lineage.edges.notProbabilistic')}
      </span>
    );
  }
  return <>{`${String(percent)}%`}</>;
}

/* The inferred-edge review queue, rendered only when there is something in it.
 *
 * **It used to render its own empty state, and that was the wrong call on this screen.** The guidance
 * was accurate — "no inference adapter is configured in this deployment, so nothing can propose an
 * edge to review" — and it is a sentence about *deployment configuration* sitting under a screen whose
 * subject is a customer's graph. On every deployment that has no inference adapter, which is all of
 * them today, it was a permanent half-page explaining why a feature nobody asked about is empty.
 *
 * **`null` rather than deleting the component**, which is the distinction that matters. A deployment
 * that wires an inference adapter genuinely needs this table: a `NEEDS_REVIEW` edge is excluded from
 * every impact answer until a human decides, so with no surface to decide on, an inferred edge is
 * stuck for ever. Deleting it would take that away from the deployments that will have one; hiding
 * an *empty* queue takes nothing from anybody.
 *
 * A failed read still renders, and that is deliberate rather than inconsistent: "the queue could not
 * be read" is not the same as "the queue is empty", and silently rendering nothing for the first
 * would tell a reviewer there is nothing to review when the truth is that nobody looked.
 */
function ReviewQueue(): ReactElement | null {
  const { t } = useTranslation();
  const queue = useReviewQueue();
  const review = useReviewEdge();

  /* Pending is also nothing-yet: a card that flashes a skeleton and then vanishes on every visit to
     this screen is worse than one that appears when it has content. */
  if (queue.isPending) return null;
  if (queue.isSuccess && queue.data.edges.length === 0) return null;

  return (
    <Card title={t('lineage.review.title')} hint={t('lineage.review.hint')}>
      {review.isError ? (
        <Alert tone="error" title={t('lineage.review.errorTitle')}>
          {t('lineage.review.errorBody')}
        </Alert>
      ) : null}

      {queue.isError ? (
        <Alert tone="error" title={t('lineage.review.title')}>
          {t('lineage.answer.error')}
        </Alert>
      ) : (
        <DataTable
          caption={t('lineage.review.caption')}
          columns={[
            { key: 'flow', label: t('lineage.edges.flow') },
            { key: 'confidence', label: t('lineage.edges.confidence'), numeric: true },
            { key: 'verdict', label: t('lineage.review.verdict'), actions: true },
          ]}
        >
          {queue.data.edges.map((edge) => (
            <tr key={edge.id}>
              {/* A `<td>`, for the reason `EdgeTable` states above. This one has never rendered on
                  any deployment — the queue is empty without an inference adapter — so it is the
                  same defect in a branch that is not merely unrun but unreachable. Fixed together,
                  because fixing one of two identical mistakes is how the other becomes permanent. */}
              <td>
                <Endpoints edge={edge} />
              </td>
              <td style={{ textAlign: 'right' }}>
                <ConfidenceCell edge={edge} />
              </td>
              <td style={{ textAlign: 'right' }}>
                <Button
                  tone="primary"
                  onClick={() => {
                    review.mutate({ edgeId: edge.id, accepted: true });
                  }}
                  disabled={review.isPending}
                >
                  {t('lineage.review.accept')}
                </Button>{' '}
                <Button
                  tone="secondary"
                  onClick={() => {
                    review.mutate({ edgeId: edge.id, accepted: false });
                  }}
                  disabled={review.isPending}
                >
                  {t('lineage.review.reject')}
                </Button>
              </td>
            </tr>
          ))}
        </DataTable>
      )}
    </Card>
  );
}
